import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBell,
  faBuilding,
  faCheckCircle,
  faEnvelope,
  faEye,
  faFilePdf,
  faFolderOpen,
  faLock,
  faPencilAlt,
  faShieldAlt,
  faTrash,
  faUnlockAlt,
  faUpload,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { SessionContext } from '../../shared/session';
import { JUSTIFICACIONES_LIBERACION } from '../../config/Parametros';
import Header from '../../shared/Header';
import Spinner from '../../shared/Spinner';
import PldBusquedaDialog from '../../shared/PldBusquedaDialog';
import DocumentosTransaccionOperacion from './DocumentosTransaccionOperacion';
import { pdf } from '@react-pdf/renderer';
import { PDFDocument, degrees, rgb } from 'pdf-lib';
import {
  deleteNodo,
  listNodosByOperacion,
  listOperacionSujetos,
  listOperaciones,
  resolvePldDocumentoFetchUrl,
  resolveOperacionDocumentoFetchUrl,
  updateOperacion,
  updateSujeto,
  uploadDocumentoOperacion,
} from '../api/pldApi';
import { PortadaAuditoria, SeparadorOperacion, SeparadorSujeto } from '../pdf/MasterPDF';
import { determinarPerfilSujeto } from '../utils/determinarPerfil';
import { esCodigoGafi, getCodigosGafi } from '../utils/paisesGafi';
import Requester from '../../shared/Requester';

function folioExpediente(operacion) {
  const num = operacion && operacion.num_expediente;
  if (num != null) return ` EXP-${String(num).padStart(4, '0')}`;
  const s = String((operacion && (operacion.id || operacion._id)) || '');
  return ` EXP-${s.slice(0, 8).toUpperCase()}`;
}

/** Pendiente = sin consulta QEQ; Alto/Bajo según coincidencia en listas. */
function riesgoPldDesdeQeq(pldListado) {
  if (pldListado === true) {
    return { etiqueta: 'Alto', color: 'danger' };
  }
  if (pldListado === false) {
    return { etiqueta: 'Bajo', color: 'success' };
  }
  return { etiqueta: 'Pendiente', color: 'warning' };
}

function mergeSujetosConNodos(sujetosRows, nodos) {
  const bySujeto = {};
  if (Array.isArray(nodos)) {
    nodos.forEach((n) => {
      bySujeto[String(n.sujeto_id)] = n;
    });
  }
  return (sujetosRows || []).map((s) => {
    const n = bySujeto[String(s.id_sujeto)];
    return {
      ...s,
      es_bc: n ? !!n.es_bc : false,
      origen_bc: n && n.origen_bc ? n.origen_bc : '',
    };
  });
}

function obtenerChecklistParaSujeto(perfilActual, esBC) {
  if (perfilActual === 'SOLO_DATOS') return [];
  let docs = [];
  if (perfilActual === 'SOCIEDAD') {
    docs = [
      { id: 'acta_constitutiva', titulo: 'Acta constitutiva' },
      { id: 'esquema_accionario', titulo: 'Esquema accionario' },
      { id: 'csf_pm', titulo: 'Cédula de Identificación Fiscal / RFC' },
      { id: 'poder', titulo: 'Poder del representante' },
      { id: 'id_representante', titulo: 'ID del representante' },
      { id: 'domicilio_pm', titulo: 'Comprobante de domicilio' },
    ];
  } else if (perfilActual === 'DELEGADO' || perfilActual === 'REPRESENTANTE LEGAL') {
    docs = [
      { id: 'id_oficial', titulo: 'Identificación oficial' },
      { id: 'domicilio', titulo: 'Comprobante de domicilio' },
      { id: 'poder', titulo: 'Carta poder / nombramiento' },
    ];
  } else {
    docs = [
      { id: 'id_oficial', titulo: 'Identificación oficial' },
      { id: 'curp', titulo: 'CURP' },
      { id: 'rfc', titulo: 'Cédula de Identificación Fiscal' },
      { id: 'domicilio', titulo: 'Comprobante de domicilio' },
    ];
  }
  if (!esBC) {
    docs.push({ id: 'formato_id_fisico', titulo: 'Formato de identificación' });
  }
  if (esBC && perfilActual !== 'SOCIEDAD') {
    docs.push({
      id: 'formato_bc_fisico',
      titulo: 'Declaración dueño beneficiario',
    });
  }
  return docs;
}

function drawMarcaCotejado(pagina) {
  const texto = 'COTEJADO CON SU ORIGINAL';
  const size = 14;
  const { height, width } = pagina.getSize();
  const anchoTexto = texto.length * size * 0.55;
  pagina.drawText(texto, {
    x: width - 10,
    y: height / 2 - anchoTexto / 2,
    size,
    color: rgb(0.8, 0, 0),
    opacity: 0.7,
    rotate: degrees(90),
  });
}

export default function DetalleOperacion({ unidadId: unidadIdProp }) {
  const { unidad: unidadRoute, operacionId } = useParams();
  const history = useHistory();
  const session = useContext(SessionContext);
  const esInmobiliaria = session.unidad?.actividad === 'inmobiliaria';

  const unidadId =
    unidadIdProp ||
    unidadRoute ||
    (session.unidad && session.unidad._id) ||
    null;

  const [operacion, setOperacion] = useState(null);
  const [sujetosOperacion, setSujetosOperacion] = useState([]);
  const [nodosOperacion, setNodosOperacion] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [isWorking, setIsWorking] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [imprimiendoMaster, setImprimiendoMaster] = useState(false);
  const [mostrarAcciones, setMostrarAcciones] = useState(true);
  const [subiendoAviso, setSubiendoAviso] = useState(null);

  // REC — liberación de operación
  const [liberacion, setLiberacion] = useState(null);
  const [justificacion, setJustificacion] = useState('');
  const [comentariosRec, setComentariosRec] = useState('');
  const [liberando, setLiberando] = useState(false);

  const puedeExpedientesWrite =
    session?.permisos?.admin || session?.permisos?.expedientesPLD === 'write';
  const puedeDocumentos =
    session?.permisos?.admin || session?.permisos?.documentosPLD === 'write';

  // Devuelve true si el sujeto ya tiene una alerta del tipo indicado en esta operación
  function sujetoYaAlertado(idSujeto, tipo) {
    return alertas.some((a) =>
      (!tipo || a.tipo === tipo) &&
      (a.sujetos || []).some((s) => String(s.id_sujeto) === String(idSujeto))
    );
  }

  async function bloquearOperacion(sujetosAltoRiesgo, descripcion, tipo = 'riesgo_alto_pld') {
    try {
      const sujetosPayload = sujetosAltoRiesgo.map((s) => ({
        id_sujeto: String(s.id_sujeto),
        nombre: s.nombre,
        pld_listado: s.pld_listado,
        pld_pais_nacionalidad: s.pld_pais_nacionalidad,
      }));
      const res = await Requester.post('alertas-pld', {
        operacion_id: operacionId,
        descripcion,
        tipo,
        sujetos: sujetosPayload,
      });
      // El click en "Entendido" del Swal es el acknowledge — se registra de inmediato
      const alertaId = res?.data?.id;
      if (alertaId) {
        await Requester.patch(`alertas-pld/${alertaId}/ack`).catch(() => { });
        // Sincronizar alertas locales para que future checks de deduplicación funcionen
        setAlertas((prev) => [...prev, {
          _id: alertaId,
          descripcion,
          tipo,
          sujetos: sujetosPayload,
          ack: true,
          timestamp_utc: new Date().toISOString(),
        }]);
      }
    } catch { /* la alerta es best-effort; el bloqueo visual procede igual */ }

    try {
      await updateOperacion(unidadId, operacionId, { estado: 'BLOQUEO TEMPORAL' });
    } catch { /* idem */ }

    setOperacion((prev) => prev ? { ...prev, estado: 'BLOQUEO TEMPORAL' } : prev);
  }

  function sincronizarPldListadoSujeto(idSujeto, listado) {
    if (typeof listado !== 'boolean' || !idSujeto) return;

    if (listado === true && !sujetoYaAlertado(idSujeto, 'riesgo_alto_pld')) {
      const sujeto = sujetosOperacion.find(
        (s) => String(s.id_sujeto) === String(idSujeto)
      );
      const descripcion = sujeto?.nombre
        ? `Sujeto "${sujeto.nombre}" detectado en listas de control PLD.`
        : 'Sujeto detectado en listas de control PLD.';

      Swal.fire({
        icon: 'warning',
        title: '⚠️ Riesgo ALTO detectado',
        html: sujeto?.nombre
          ? `El sujeto <strong>${sujeto.nombre}</strong> aparece en listas de control.<br/>Se requiere confirmación del REC.`
          : 'El sujeto aparece en listas de control.<br/>Se requiere revisión por parte del REC.',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#dc3545',
      }).then(() => bloquearOperacion(sujeto ? [sujeto] : [], descripcion, 'riesgo_alto_pld'));
    }

    setSujetosOperacion((prev) =>
      prev.map((s) =>
        String(s.id_sujeto) === String(idSujeto) ? { ...s, pld_listado: listado } : s
      )
    );
    if (unidadId) {
      updateSujeto(unidadId, idSujeto, {
        pld_listado: listado,
        pld_consulta_listas_fecha: new Date().toISOString(),
      }).catch(() => { });
    }
  }

  const filas = useMemo(
    () => mergeSujetosConNodos(sujetosOperacion, nodosOperacion),
    [sujetosOperacion, nodosOperacion]
  );

  const esRepresentacionParticipaciones =
    operacion && operacion.tipo_representacion_capital === 'participaciones';

  useEffect(() => {
    if (!unidadId || !operacionId) return undefined;
    let cancelled = false;
    async function cargar() {
      setIsWorking(true);
      setLoadError(null);
      try {
        const [ops, nodos, sujetos, alertasRes, liberacionRes] = await Promise.all([
          listOperaciones(unidadId),
          listNodosByOperacion(unidadId, operacionId),
          listOperacionSujetos(unidadId, operacionId),
          Requester.get(`alertas-pld?operacion_id=${operacionId}`).catch(() => ({ data: { alertas: [] } })),
          Requester.get(`liberaciones-pld?operacion_id=${operacionId}`).catch(() => ({ data: { liberacion: null } })),
        ]);
        if (cancelled) return;
        const op = Array.isArray(ops)
          ? ops.find((o) => String(o.id) === String(operacionId))
          : null;
        setOperacion(op || null);
        setMostrarAcciones(op && op.tipo_operacion && !op.tipo_operacion.includes('RATIFICACIÓN DE CRÉDITOS'));
        setNodosOperacion(Array.isArray(nodos) ? nodos : []);
        const sujetosValidos = Array.isArray(sujetos) ? sujetos : [];
        setSujetosOperacion(sujetosValidos);

        const alertasExistentes = alertasRes?.data?.alertas || [];
        setAlertas(alertasExistentes);
        setLiberacion(liberacionRes?.data?.liberacion || null);

        function idsAlertadosPorTipo(tipo) {
          return new Set(
            alertasExistentes
              .filter((a) => a.tipo === tipo)
              .flatMap((a) => (a.sujetos || []).map((s) => String(s.id_sujeto)))
          );
        }

        const idsListasAlertados = idsAlertadosPorTipo('riesgo_alto_pld');
        const sujetosListasNuevos = sujetosValidos.filter(
          (s) => s.pld_listado === true && !idsListasAlertados.has(String(s.id_sujeto))
        );

        const gafiSet = await getCodigosGafi();
        const idsGafiAlertados = idsAlertadosPorTipo('jurisdiccion_gafi');
        const sujetosGafiNuevos = sujetosValidos.filter(
          (s) =>
            esCodigoGafi(s.pld_pais_nacionalidad, gafiSet) &&
            !idsGafiAlertados.has(String(s.id_sujeto))
        );

        if (sujetosListasNuevos.length > 0) {
          const nombres = sujetosListasNuevos.map((s) => `• ${s.nombre}`).join('<br/>');
          const descripcion = `Apertura de operación con ${sujetosListasNuevos.length} sujeto(s) nuevo(s) en listas de control: ${sujetosListasNuevos.map((s) => s.nombre).join(', ')}.`;
          await Swal.fire({
            icon: 'warning',
            title: '⚠️ Riesgo ALTO detectado',
            html: `Los siguientes sujetos aparecen en listas de control:<br/><br/><strong>${nombres}</strong><br/><br/>Se requiere confirmación/revisión por parte del REC.`,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#dc3545',
          });
          if (!cancelled) {
            await bloquearOperacion(sujetosListasNuevos, descripcion, 'riesgo_alto_pld');
          }
        }

        if (!cancelled && sujetosGafiNuevos.length > 0) {
          const nombres = sujetosGafiNuevos
            .map((s) => `• ${s.nombre} (${s.nacionalidad || s.pld_pais_nacionalidad})`)
            .join('<br/>');
          const descripcion = `Sujeto(s) con nacionalidad en jurisdicción GAFI de alto riesgo: ${sujetosGafiNuevos.map((s) => s.nombre).join(', ')}.`;
          await Swal.fire({
            icon: 'warning',
            title: '⚠️ Jurisdicción de Alto Riesgo — GAFI',
            html: `Los siguientes sujetos tienen nacionalidad en listas GAFI:<br/><br/><strong>${nombres}</strong><br/><br/>Se requiere debida diligencia reforzada y revisión del REC.`,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#dc3545',
          });
          if (!cancelled) {
            await bloquearOperacion(sujetosGafiNuevos, descripcion, 'jurisdiccion_gafi');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError('No se pudo cargar el expediente.');
          setOperacion(null);
          setSujetosOperacion([]);
          setNodosOperacion([]);
        }
      } finally {
        if (!cancelled) setIsWorking(false);
      }
    }
    cargar();
    return () => {
      cancelled = true;
    };
  }, [unidadId, operacionId]);

  async function eliminarSujeto(idRelacion, nombreSujeto) {
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'Quitar sujeto',
      html: `¿Confirma quitar a <strong>${nombreSujeto}</strong> de esta operación?`,
      showCancelButton: true,
      confirmButtonText: 'Sí, quitar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
    });
    if (!ok.isConfirmed || !unidadId) return;
    const prev = sujetosOperacion.slice();
    const prevNodos = nodosOperacion.slice();
    setSujetosOperacion((s) => s.filter((x) => x.id_relacion !== idRelacion));
    setNodosOperacion((n) => n.filter((x) => String(x.id) !== String(idRelacion)));
    try {
      await deleteNodo(unidadId, idRelacion);
    } catch (err) {
      setSujetosOperacion(prev);
      setNodosOperacion(prevNodos);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.error) ||
          err.message ||
          'No se pudo eliminar la relación.',
      });
    }
  }

  async function handleLiberarOperacion() {
    if (!justificacion) return;

    const etiquetaSeleccionada = JUSTIFICACIONES_LIBERACION.find((j) => j.id === justificacion)?.label || justificacion;

    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Confirmar liberación?',
      html: `Justificación: <strong>${etiquetaSeleccionada}</strong><br/>${comentariosRec ? `<br/>Comentarios: ${comentariosRec}` : ''}`,
      showCancelButton: true,
      confirmButtonText: 'Sí, liberar operación',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#198754',
    });
    if (!confirm.isConfirmed) return;

    setLiberando(true);
    try {
      const res = await Requester.post('liberaciones-pld', {
        operacion_id: operacionId,
        justificacion,
        comentarios: comentariosRec,
      });
      setLiberacion(res?.data?.liberacion || null);
      setOperacion((prev) => prev ? { ...prev, estado: 'LIBERADA' } : prev);
      await updateOperacion(unidadId, operacionId, { estado: 'En Integración' }).catch(() => { });
      await Swal.fire({
        icon: 'success',
        title: 'Operación liberada',
        text: 'La liberación quedó registrada correctamente.',
        timer: 2500,
        showConfirmButton: false,
      });
    } catch {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo registrar la liberación. Intenta de nuevo.',
      });
    } finally {
      setLiberando(false);
    }
  }

  function documentosFaltantes(filas) {
    let faltantes = 0;
    let sujetosFaltantes = 0;
    let texto = '';
    filas.forEach((sujeto) => {
      const perfil = sujeto?.tipo_persona?.toLowerCase() !== 'fisica' ? 'SOCIEDAD' : determinarPerfilSujeto(sujeto.rol, sujeto.es_bc, esInmobiliaria);
      const reglas = obtenerChecklistParaSujeto(perfil, sujeto.es_bc);
      const subidos = sujeto.documentos_status || {};
      const docsQueFaltan = [];
      reglas.forEach((req) => {
        if (!subidos[req.id] || !subidos[req.id].url) {
          docsQueFaltan.push(`- ${req.titulo}`);
          faltantes += 1;
        }
      });
      if (docsQueFaltan.length > 0) {
        sujetosFaltantes += 1;
        texto += `${sujeto.nombre} (${sujeto.rol}):\n`;
        texto += `${docsQueFaltan.join('\n')}\n\n`;
      }
    });

    return { faltantes, sujetosFaltantes, texto };
  }

  function generarCorreoAuditoria() {
    if (!operacion) return;

    let cuerpoCorreo = `Hola,\n\nPara poder continuar con la integración del expediente de "${operacion.denominacion}", necesitamos que nos apoyen enviando la siguiente documentación que se encuentra pendiente:\n\n`;

    const { faltantes, texto } = documentosFaltantes(filas);

    if (faltantes === 0) {
      Swal.fire({
        icon: 'success',
        title: 'Expediente completo',
        text: 'No hay documentos faltantes según el checklist por perfil.',
      });
      return;
    } else {
      cuerpoCorreo += texto;
    }

    cuerpoCorreo +=
      'Quedamos a la espera para poder avanzar con el trámite.\n\nSaludos cordiales.';

    const asunto = `Documentación pendiente - Expediente ${operacion.denominacion}`;

    const abrirCorreo = () => {
      window.location.href = `mailto:?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpoCorreo)}`;
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(cuerpoCorreo).then(
        () => {
          Swal.fire({
            icon: 'info',
            title: 'Cuerpo copiado al portapapeles',
            html:
              'Se abrirá tu cliente de correo con el asunto ya listo.<br>' +
              '<strong>Pega el cuerpo</strong> con Ctrl+V en el campo de texto del correo.',
            confirmButtonText: 'Abrir correo',
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
          }).then((r) => {
            if (r.isConfirmed) abrirCorreo();
          });
        },
        () => abrirCorreo()
      );
    } else {
      abrirCorreo();
    }
  }

  async function armarMasterPDF() {
    if (!operacion || !unidadId) return;
    try {
      setImprimiendoMaster(true);
      const pdfMaestro = await PDFDocument.create();

      const { sujetosFaltantes, texto: textoFaltantes } = documentosFaltantes(filas);
      const portadaBlob = await pdf(
        <PortadaAuditoria
          operacion={operacion}
          sujetos={filas}
          sujetosFaltantes={sujetosFaltantes}
          textoFaltantes={textoFaltantes}
        />
      ).toBlob();
      const portadaDoc = await PDFDocument.load(await portadaBlob.arrayBuffer());
      const paginasPortada = await pdfMaestro.copyPages(
        portadaDoc,
        portadaDoc.getPageIndices()
      );
      paginasPortada.forEach((pagina) => pdfMaestro.addPage(pagina));

      // ── Documentos de la operación ────────────────────────────────────────
      const docsOperacion = [
        { key: 'contrato_intermediacion', label: 'Contrato de intermediación', doc: operacion.contrato_intermediacion },
        { key: 'contrato_operacion', label: 'Contrato de operación', doc: operacion.contrato_operacion },
        ...((operacion.comprobantes_pago || []).map((c, i) => ({
          key: `comprobante_pago_${i}`,
          label: `Comprobante de pago ${i + 1}`,
          doc: c,
        }))),
      ].filter(({ doc }) => resolveOperacionDocumentoFetchUrl(unidadId, operacionId, doc));

      if (docsOperacion.length > 0) {
        const sepOpBlob = await pdf(<SeparadorOperacion operacion={operacion} />).toBlob();
        const sepOpDoc = await PDFDocument.load(await sepOpBlob.arrayBuffer());
        const paginasSepOp = await pdfMaestro.copyPages(sepOpDoc, sepOpDoc.getPageIndices());
        paginasSepOp.forEach((pagina) => pdfMaestro.addPage(pagina));

        for (const { key, doc } of docsOperacion) {
          try {
            const docUrl = resolveOperacionDocumentoFetchUrl(unidadId, operacionId, doc);
            if (!docUrl) continue;
            const respuesta = await fetch(docUrl, { credentials: 'include' });
            if (!respuesta.ok) continue;
            const docArrayBuffer = await respuesta.arrayBuffer();
            const contentType = (respuesta.headers.get('content-type') || '').toLowerCase();
            const metaNombre = `${doc.fileUrl || ''} ${doc.nombreArchivo || ''} ${doc.url || ''}`;
            const urlLower = metaNombre.toLowerCase();

            const esPdf = contentType.includes('application/pdf') || urlLower.includes('.pdf');
            const esImagen =
              contentType.includes('image/png') || contentType.includes('image/jpeg') ||
              contentType.includes('image/jpg') || urlLower.includes('.png') ||
              urlLower.includes('.jpg') || urlLower.includes('.jpeg');

            if (esPdf) {
              const pdfExterno = await PDFDocument.load(docArrayBuffer);
              const paginasCopiadas = await pdfMaestro.copyPages(pdfExterno, pdfExterno.getPageIndices());
              paginasCopiadas.forEach((pagina) => pdfMaestro.addPage(pagina));
            } else if (esImagen) {
              const nuevaPagina = pdfMaestro.addPage();
              let imagen;
              if (contentType.includes('png') || urlLower.includes('.png')) {
                imagen = await pdfMaestro.embedPng(docArrayBuffer);
              } else {
                imagen = await pdfMaestro.embedJpg(docArrayBuffer);
              }
              const { width } = nuevaPagina.getSize();
              const anchoDisponible = width - 100;
              const ratio = imagen.height / imagen.width;
              nuevaPagina.drawImage(imagen, {
                x: 50,
                y: 50,
                width: anchoDisponible,
                height: anchoDisponible * ratio,
              });
            }
          } catch (err) {
            console.error(`Error al adjuntar documento de operación ${key}:`, err);
          }
        }
      }

      for (const sujeto of filas) {
        const sujetoId = sujeto.id_sujeto || sujeto.id;
        const tieneAnexos =
          sujeto.documentos_status &&
          Object.values(sujeto.documentos_status).some((doc) =>
            resolvePldDocumentoFetchUrl(unidadId, sujetoId, doc)
          );
        if (!tieneAnexos) continue;

        const separadorBlob = await pdf(<SeparadorSujeto sujeto={sujeto} />).toBlob();
        const separadorDoc = await PDFDocument.load(await separadorBlob.arrayBuffer());
        const paginasSeparador = await pdfMaestro.copyPages(
          separadorDoc,
          separadorDoc.getPageIndices()
        );
        paginasSeparador.forEach((pagina) => pdfMaestro.addPage(pagina));

        if (sujeto.documentos_status) {
          for (const [key, doc] of Object.entries(sujeto.documentos_status)) {
            try {
              const docUrl = resolvePldDocumentoFetchUrl(unidadId, sujetoId, doc);
              if (!docUrl) continue;
              const respuesta = await fetch(docUrl, { credentials: 'include' });
              if (!respuesta.ok) continue;
              const docArrayBuffer = await respuesta.arrayBuffer();
              const contentType = (
                respuesta.headers.get('content-type') || ''
              ).toLowerCase();
              const metaNombre = `${doc.fileUrl || ''} ${doc.nombreArchivo || ''} ${doc.url || ''}`;
              const urlLower = metaNombre.toLowerCase();

              const esPdf =
                contentType.includes('application/pdf') ||
                urlLower.includes('.pdf');
              const esImagen =
                contentType.includes('image/png') ||
                contentType.includes('image/jpeg') ||
                contentType.includes('image/jpg') ||
                urlLower.includes('.png') ||
                urlLower.includes('.jpg') ||
                urlLower.includes('.jpeg');

              if (esPdf) {
                const pdfExterno = await PDFDocument.load(docArrayBuffer);
                if (doc.cotejado) {
                  const paginas = pdfExterno.getPages();
                  paginas.forEach((pagina) => drawMarcaCotejado(pagina));
                }
                const paginasCopiadas = await pdfMaestro.copyPages(
                  pdfExterno,
                  pdfExterno.getPageIndices()
                );
                paginasCopiadas.forEach((pagina) => pdfMaestro.addPage(pagina));
              } else if (esImagen) {
                const nuevaPagina = pdfMaestro.addPage();
                let imagen;
                if (contentType.includes('png') || urlLower.includes('.png')) {
                  imagen = await pdfMaestro.embedPng(docArrayBuffer);
                } else {
                  imagen = await pdfMaestro.embedJpg(docArrayBuffer);
                }
                const { width } = nuevaPagina.getSize();
                const anchoDisponible = width - 100;
                const ratio = imagen.height / imagen.width;
                const altoImagen = anchoDisponible * ratio;
                nuevaPagina.drawImage(imagen, {
                  x: 50,
                  y: 50,
                  width: anchoDisponible,
                  height: altoImagen,
                });
                if (doc.cotejado) {
                  drawMarcaCotejado(nuevaPagina);
                }
              }
            } catch (err) {
              console.error(`Error al adjuntar el documento ${key}:`, err);
            }
          }
        }
      }

      const pdfFinalBytes = await pdfMaestro.save();
      const blobFinal = new Blob([pdfFinalBytes], { type: 'application/pdf' });
      const nombreBase = (operacion.denominacion || 'expediente')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '_')
        .slice(0, 80);
      const linkDescarga = document.createElement('a');
      linkDescarga.href = URL.createObjectURL(blobFinal);
      linkDescarga.download = `AUDITORIA_${nombreBase}.pdf`;
      linkDescarga.click();
      URL.revokeObjectURL(linkDescarga.href);
    } catch (error) {
      console.error('Error al armar el Master PDF:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Master PDF',
        text: 'No se pudo compilar el expediente. Revise la consola o intente de nuevo.',
      });
    } finally {
      setImprimiendoMaster(false);
    }
  }

  async function handleUploadAviso(e, docId) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !unidadId || !operacionId) return;
    setSubiendoAviso(docId);
    try {
      const res = await uploadDocumentoOperacion(unidadId, operacionId, docId, file);
      if (res && res.success) {
        setOperacion((prev) => prev ? { ...prev, [docId]: res.doc } : prev);
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Subida',
          text: (res && res.error) || 'No se pudo subir el archivo.',
        });
      }
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          (err && err.response && err.response.data && err.response.data.error) ||
          err.message ||
          'No se pudo subir el archivo.',
      });
    } finally {
      setSubiendoAviso(null);
    }
  }

  if (!unidadId || !operacionId) {
    return (
      <Container className="py-4">
        <p className="text-danger">Falta identificador de unidad u operación.</p>
      </Container>
    );
  }

  return (
    <Container>
      <Button
        color="link"
        type="button"
        className="pl-0 mb-2"
        onClick={() => history.push(`/${unidadId}/pld`)}
      >
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
        Regresar al tablero
      </Button>

      <Header>Expediente PLD</Header>

      <Spinner isWorking={isWorking} />
      {loadError ? <p className="text-danger">{loadError}</p> : null}

      {!isWorking && !operacion && !loadError ? (
        <p className="text-muted">No se encontró la operación en esta unidad.</p>
      ) : null}

      {operacion ? (
        <>
          {alertas.length > 0 && (
            <Card className="shadow-sm mb-4 border-danger">
              <CardHeader className="d-flex align-items-center bg-danger text-white">
                <FontAwesomeIcon icon={faBell} className="mr-2" />
                <strong>Alertas PLD registradas ({alertas.length})</strong>
              </CardHeader>
              <CardBody className="p-0">
                <Table responsive borderless size="sm" className="mb-0">
                  <thead className="bg-light text-muted small text-uppercase">
                    <tr>
                      <th className="pl-3">Fecha</th>
                      <th>Descripción</th>
                      <th>Sujetos</th>
                      <th className="text-center">Enterado</th>
                      <th>Por</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alertas.map((alerta) => (
                      <tr key={alerta._id}>
                        <td className="pl-3 text-nowrap small text-muted">
                          {alerta.timestamp_utc
                            ? new Date(alerta.timestamp_utc).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
                            : '—'}
                        </td>
                        <td className="small">{alerta.descripcion}</td>
                        <td>
                          {(alerta.sujetos || []).map((s) => (
                            <Badge key={s.id_sujeto} color="danger" pill className="mr-1 text-uppercase" style={{ fontSize: '0.7em' }}>
                              {s.nombre}
                            </Badge>
                          ))}
                        </td>
                        <td className="text-center">
                          {alerta.ack
                            ? <FontAwesomeIcon icon={faCheckCircle} className="text-success" title="Reconocida" />
                            : <span className="text-muted small">Pendiente</span>}
                        </td>
                        <td className="small text-muted">
                          {alerta.timestamp_ack
                            ? <>
                              {alerta.usuario_ack?.nombre || '—'}
                              <span className="d-block" style={{ fontSize: '0.8em' }}>
                                {new Date(alerta.timestamp_ack).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                              </span>
                            </>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}

          <Row className="mb-4">
            <Col md={esInmobiliaria ? '8' : '12'}>
              <Card className="mb-3 shadow-sm border-left border-danger">
                <CardBody className="d-flex flex-wrap justify-content-between align-items-center">
                  <div>
                    <h3 className="text-uppercase mb-2">
                      {
                        esInmobiliaria ? operacion.asunto
                          : (<>{operacion.tipo_operacion} {operacion.denominacion}</>)
                      }

                    </h3>
                    <div className="small text-muted">
                      <span>
                        <strong>Operación:</strong> {operacion.tipo_operacion}
                      </span>
                      <span className="m-3">
                        <strong>Folio:</strong>{folioExpediente(operacion)}
                      </span>
                    </div>
                  </div>
                  <Badge
                    color={
                      operacion.estado === 'En Integración' ? 'success'
                        : operacion.estado === 'BLOQUEO TEMPORAL' ? 'danger'
                          : 'warning'
                    }
                    pill
                    className="p-2 text-uppercase"
                  >
                    {(operacion.estado || 'Faltan participantes').toUpperCase()}
                  </Badge>
                </CardBody>
              </Card>
              {esInmobiliaria ? (
                <Col lg="12">
                  <DocumentosTransaccionOperacion
                    operacion={operacion}
                    setOperacion={setOperacion}
                    unidadId={unidadId}
                    operacionId={operacionId}
                    puedeDocumentos={puedeDocumentos}
                  />
                </Col>
              ) : null}
            </Col>
            <Col lg="8" hidden={esInmobiliaria}>
              <Card className="shadow-sm h-100">
                <CardHeader className="d-flex align-items-center border-bottom">
                  <FontAwesomeIcon
                    icon={faBuilding}
                    className="text-danger mr-2"
                  />
                  <strong>1. Datos base de la sociedad</strong>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="6">
                      <label className="small text-muted text-uppercase font-weight-bold">
                        Denominación
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm font-weight-bold text-uppercase bg-light"
                        value={operacion.denominacion || ''}
                        readOnly
                        disabled
                      />
                    </Col>
                    <Col md="6" hidden={!mostrarAcciones}>
                      <div className="bg-light border rounded p-3">
                        <h6 className="font-weight-bold">Estructura de capital</h6>
                        <Row>
                          <Col xs="6">
                            <label className="small text-muted text-uppercase">
                              Cap. fijo ($)
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={operacion.capital_fijo ?? '0'}
                              readOnly
                              disabled
                            />
                          </Col>
                          <Col xs="6">
                            <label className="small text-muted text-uppercase">
                              Cap. variable ($)
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={operacion.capital_variable ?? '0'}
                              readOnly
                              disabled
                            />
                          </Col>
                        </Row>
                        {esRepresentacionParticipaciones ? (
                          <>
                            <label className="small text-muted text-uppercase font-weight-bold mt-2">
                              Representación
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm mb-2"
                              value="Partes Sociales"
                              readOnly
                              disabled
                            />
                          </>
                        ) : (
                          <>
                            <label className="small text-danger text-uppercase font-weight-bold mt-2">
                              Total acciones
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm font-weight-bold"
                              value={operacion.total_acciones ?? '0'}
                              readOnly
                              disabled
                            />
                          </>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col lg="4">
              <Card className="shadow-sm bg-light h-100">
                <CardHeader className="py-2">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-danger mr-2" />
                  <strong className="small text-uppercase">Gestión PLD</strong>
                </CardHeader>
                <CardBody>
                  {['vista_aviso', 'acuse_aviso'].map((docId) => {
                    const etiqueta = docId === 'vista_aviso' ? 'Vista aviso' : 'Acuse aviso';
                    const doc = operacion[docId];
                    const tieneArchivo = doc && doc.fileUrl;
                    const esteSubiendo = subiendoAviso === docId;
                    const docUrl = tieneArchivo
                      ? resolveOperacionDocumentoFetchUrl(unidadId, operacionId, doc)
                      : null;
                    return (
                      <div key={docId} className="mb-2">
                        <label
                          className={`btn btn-block d-flex justify-content-between align-items-center mb-0${tieneArchivo ? ' btn-success' : ' btn-outline-secondary'}${esteSubiendo ? ' disabled' : ''}`}
                          style={{ cursor: esteSubiendo ? 'not-allowed' : 'pointer', fontWeight: 'normal', fontSize: '0.875rem' }}
                        >
                          <span>
                            <FontAwesomeIcon
                              icon={esteSubiendo ? faUpload : tieneArchivo ? faCheckCircle : faUpload}
                              spin={esteSubiendo}
                              className="mr-2"
                            />
                            {esteSubiendo ? 'Subiendo…' : etiqueta}
                          </span>
                          <input
                            type="file"
                            className="d-none"
                            accept="application/pdf,image/*"
                            disabled={esteSubiendo}
                            onChange={(e) => handleUploadAviso(e, docId)}
                          />
                        </label>
                        {tieneArchivo && docUrl && (
                          <a
                            href={docUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-link btn-sm p-0 mt-1 d-flex align-items-center"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            <small>Ver archivo</small>
                          </a>
                        )}
                      </div>
                    );
                  })}
                  <hr />
                  <Button
                    color="info"
                    outline
                    block
                    className="mb-2 font-weight-bold small"
                    type="button"
                    onClick={generarCorreoAuditoria}
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                    Solicitar pendientes (correo)
                  </Button>
                  <Button
                    color="primary"
                    block
                    className="font-weight-bold small"
                    type="button"
                    disabled={imprimiendoMaster}
                    onClick={armarMasterPDF}
                  >
                    <FontAwesomeIcon
                      icon={faFilePdf}
                      spin={imprimiendoMaster}
                      className="mr-2"
                    />
                    {imprimiendoMaster ? 'Generando…' : 'Descargar Master PDF'}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm mb-3">
            <CardHeader className="d-flex flex-wrap justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faUsers} className="text-primary mr-2" />
                <strong>Sujetos objeto de identificación</strong>
              </div>
              {puedeExpedientesWrite ? (
                <Button
                  color="primary"
                  size="sm"
                  type="button"
                  onClick={() =>
                    history.push(
                      `/${unidadId}/pld/${operacionId}/editar`,
                      { operacion }
                    )
                  }
                >
                  <FontAwesomeIcon icon={faPencilAlt} className="mr-2" />
                  Editar Sujetos
                </Button>
              ) : null}
            </CardHeader>
            <CardBody className="p-0">
              <Table responsive borderless className="mb-0" size="sm">
                <thead className="bg-light text-muted small text-uppercase">
                  <tr>
                    <th className="pl-3">Nombre</th>
                    <th>Rol</th>
                    <th>RFC</th>
                    <th className="text-center">Riesgo PLD</th>
                    <th className="text-center pr-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        No hay sujetos vinculados. Use &quot;Agregar / editar
                        socios&quot; para completar el expediente.
                      </td>
                    </tr>
                  ) : (
                    filas.map((sujeto) => (
                      <tr key={sujeto.id_relacion}>
                        <td className="pl-3 font-weight-bold text-uppercase">
                          {sujeto.nombre}
                        </td>
                        <td className="small font-weight-bold text-uppercase text-muted">
                          {sujeto.rol}
                          {sujeto.otro_especificacion ? (
                            <span className="d-block font-weight-normal text-info" style={{ fontSize: '0.8em' }}>
                              {sujeto.otro_especificacion}
                            </span>
                          ) : null}
                        </td>
                        <td className="small text-muted font-italic">
                          {sujeto.rfc}
                        </td>
                        <td className="text-center">
                          <PldBusquedaDialog
                            nombreCompleto={sujeto.nombre}
                            rfc={sujeto.rfc}
                            onListadoChange={(listado) =>
                              sincronizarPldListadoSujeto(
                                sujeto.id_sujeto,
                                listado
                              )
                            }
                            renderTrigger={({ onClick, listadoBoton }) => {
                              const pldListado =
                                typeof listadoBoton === 'boolean'
                                  ? listadoBoton
                                  : sujeto.pld_listado;
                              const riesgoBadge =
                                riesgoPldDesdeQeq(pldListado);
                              return (
                                <Badge
                                  color={riesgoBadge.color}
                                  pill
                                  className="text-uppercase"
                                  style={{ cursor: 'pointer' }}
                                  role="button"
                                  tabIndex={0}
                                  onClick={onClick}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === 'Enter' ||
                                      e.key === ' '
                                    ) {
                                      onClick(e);
                                    }
                                  }}
                                >
                                  {riesgoBadge.etiqueta}
                                </Badge>
                              );
                            }}
                          />
                        </td>
                        <td className="text-center pr-3">
                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            className="mr-1"
                            type="button"
                            onClick={() =>
                              history.push(
                                `/${unidadId}/pld/${operacionId}/sujeto/${sujeto.id_sujeto}/${sujeto.id_relacion}`
                              )
                            }
                          >
                            <FontAwesomeIcon
                              icon={faFolderOpen}
                              className="mr-1"
                            />
                            Abrir
                          </Button>
                          {puedeExpedientesWrite ? (
                            <Button
                              color="danger"
                              outline
                              size="sm"
                              type="button"
                              title="Quitar de la operación"
                              onClick={() =>
                                eliminarSujeto(
                                  sujeto.id_relacion,
                                  sujeto.nombre
                                )
                              }
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
          {alertas.length > 0 && (() => {
            // Hay alerta posterior a la última liberación → se requiere nueva liberación
            const hayAlertaNueva = liberacion
              ? alertas.some((a) => new Date(a.timestamp_utc) > new Date(liberacion.timestamp_utc))
              : true;

            const formularioVisible = !liberacion || hayAlertaNueva;

            return (
              <Card className="shadow-sm">
                <CardHeader className="d-flex flex-wrap justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon
                      icon={formularioVisible ? faLock : faUnlockAlt}
                      className={`mr-2 ${formularioVisible ? 'text-danger' : 'text-success'}`}
                    />
                    <strong>Exclusivo Responsable de Evaluación de Riesgos — REC</strong>
                  </div>
                  {formularioVisible ? (
                    <Badge color="danger" pill className="p-2">
                      <FontAwesomeIcon icon={faLock} className="mr-1" />
                      {hayAlertaNueva && liberacion ? 'NUEVA ALERTA — REQUIERE LIBERACIÓN' : 'EN BLOQUEO'}
                    </Badge>
                  ) : (
                    <Badge color="success" pill className="p-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                      OPERACIÓN LIBERADA
                    </Badge>
                  )}
                </CardHeader>
                <CardBody>
                  {/* ── Liberación anterior (visible solo cuando hay nueva alerta) ── */}
                  {hayAlertaNueva && liberacion && (
                    <div className="mb-4 p-3 bg-light border rounded">
                      <small className="text-muted d-block mb-2 fw-semibold">
                        Liberación anterior registrada
                      </small>
                      <Row>
                        <Col md={4}>
                          <small className="text-muted d-block">Liberada por</small>
                          <strong>{liberacion.usuario?.nombre || '—'}</strong>
                        </Col>
                        <Col md={4}>
                          <small className="text-muted d-block">Fecha</small>
                          <strong>
                            {liberacion.timestamp_utc
                              ? new Date(liberacion.timestamp_utc).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })
                              : '—'}
                          </strong>
                        </Col>
                        <Col md={4}>
                          <small className="text-muted d-block">Justificación</small>
                          <strong>
                            {JUSTIFICACIONES_LIBERACION.find((j) => j.id === liberacion.justificacion)?.label || liberacion.justificacion}
                          </strong>
                        </Col>
                      </Row>
                      {liberacion.comentarios && (
                        <Row className="mt-1">
                          <Col>
                            <small className="text-muted d-block">Comentarios</small>
                            <span>{liberacion.comentarios}</span>
                          </Col>
                        </Row>
                      )}
                    </div>
                  )}

                  {formularioVisible ? (
                    /* ── Formulario de liberación ── */
                    <div>
                      {hayAlertaNueva && liberacion && (
                        <p className="text-danger mb-3">
                          <FontAwesomeIcon icon={faBell} className="mr-2" />
                          Se registró una nueva alerta después de la última liberación. El REC debe justificar una nueva liberación para continuar.
                        </p>
                      )}
                      {!liberacion && (
                        <p className="text-muted mb-3">
                          Liberar la operación cuando se haya confirmado la identidad y situación de los sujetos objeto de identificación y no existan impedimentos PLD vigentes.
                        </p>
                      )}

                      <FormGroup>
                        <Label for="rec-justificacion">
                          Justificación de la liberación <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="select"
                          id="rec-justificacion"
                          value={justificacion}
                          onChange={(e) => setJustificacion(e.target.value)}
                          disabled={liberando}
                        >
                          <option value="">Selecciona una opción...</option>
                          {JUSTIFICACIONES_LIBERACION.map((j) => (
                            <option key={j.id} value={j.id}>{j.label}</option>
                          ))}
                        </Input>
                      </FormGroup>

                      <FormGroup>
                        <Label for="rec-comentarios">Comentarios del REC</Label>
                        <Input
                          type="textarea"
                          id="rec-comentarios"
                          rows={3}
                          placeholder="Describe el fundamento de la liberación..."
                          value={comentariosRec}
                          onChange={(e) => setComentariosRec(e.target.value)}
                          disabled={liberando}
                        />
                      </FormGroup>

                      <Button
                        color="success"
                        type="button"
                        className='mt-3'
                        disabled={!justificacion || liberando}
                        onClick={handleLiberarOperacion}
                      >
                        <FontAwesomeIcon icon={faUnlockAlt} spin={liberando} className="mr-2" />
                        {liberando ? 'Registrando...' : 'Liberar operación'}
                      </Button>
                    </div>
                  ) : (
                    /* ── Liberación vigente — solo lectura ── */
                    <div>
                      <p className="text-success mb-3">
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                        Esta operación fue liberada por el REC.
                      </p>
                      <Row className="mb-2">
                        <Col md={4}>
                          <small className="text-muted d-block">Liberada por</small>
                          <strong>{liberacion.usuario?.nombre || '—'}</strong>
                        </Col>
                        <Col md={4}>
                          <small className="text-muted d-block">Fecha</small>
                          <strong>
                            {liberacion.timestamp_utc
                              ? new Date(liberacion.timestamp_utc).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })
                              : '—'}
                          </strong>
                        </Col>
                        <Col md={4}>
                          <small className="text-muted d-block">Justificación</small>
                          <strong>
                            {JUSTIFICACIONES_LIBERACION.find((j) => j.id === liberacion.justificacion)?.label || liberacion.justificacion}
                          </strong>
                        </Col>
                      </Row>
                      {liberacion.comentarios && (
                        <Row>
                          <Col>
                            <small className="text-muted d-block">Comentarios del REC</small>
                            <p className="mb-0">{liberacion.comentarios}</p>
                          </Col>
                        </Row>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            );
          })()}


        </>
      ) : null}
    </Container>
  );
}
